// Default incurred/paid windows for a conditional laser (#7344, plan §3).
//
// Each condition has its own windows, enforced like contract-laser windows. They
// are prefilled from the policy and stay editable. Source order:
//   1. the policy's specific benefit eligible-expense windows;
//   2. the policy effective term, when the benefit has no window dates
//      (about a third of UAT specific-benefit rows, #7316).
// Once ADR-081 D5 is live, the D5-computed windows replace step 1.
//
// Each window falls back independently: a benefit with incurred dates but no
// paid dates takes the term for the paid window.

export interface ConditionWindows {
  incurred_start_date: string;
  incurred_end_date: string;
  paid_start_date: string;
  paid_end_date: string;
}

export type ConditionWindowSource = "specific_benefit" | "policy_term" | "mixed" | "none";

export interface ConditionWindowDefaults {
  windows: ConditionWindows;
  source: ConditionWindowSource;
}

export interface ConditionWindowDefaultInputs {
  specificBenefit?: {
    eligible_expenses_incurred_start_date?: string | null;
    eligible_expenses_incurred_end_date?: string | null;
    eligible_expenses_paid_start_date?: string | null;
    eligible_expenses_paid_end_date?: string | null;
  } | null;
  policyStart?: string | null;
  policyEnd?: string | null;
}

/** Normalise API dates ("2026-01-01" or "2026-01-01T00:00:00Z") to YYYY-MM-DD. */
export const toYMD = (value: string | null | undefined): string => (value ? value.slice(0, 10) : "");

const pair = (start?: string | null, end?: string | null) => {
  const s = toYMD(start);
  const e = toYMD(end);
  return s && e ? { start: s, end: e } : null;
};

export const resolveConditionWindowDefaults = ({
  specificBenefit,
  policyStart,
  policyEnd,
}: ConditionWindowDefaultInputs): ConditionWindowDefaults => {
  const term = pair(policyStart, policyEnd);
  const incurred = pair(
    specificBenefit?.eligible_expenses_incurred_start_date,
    specificBenefit?.eligible_expenses_incurred_end_date
  );
  const paid = pair(
    specificBenefit?.eligible_expenses_paid_start_date,
    specificBenefit?.eligible_expenses_paid_end_date
  );

  const inc = incurred ?? term;
  const pd = paid ?? term;
  const windows: ConditionWindows = {
    incurred_start_date: inc?.start ?? "",
    incurred_end_date: inc?.end ?? "",
    paid_start_date: pd?.start ?? "",
    paid_end_date: pd?.end ?? "",
  };

  let source: ConditionWindowSource;
  if (incurred && paid) source = "specific_benefit";
  else if (!incurred && !paid) source = term ? "policy_term" : "none";
  else source = term ? "mixed" : "specific_benefit";
  return { windows, source };
};

const WINDOW_KEYS: (keyof ConditionWindows)[] = [
  "incurred_start_date",
  "incurred_end_date",
  "paid_start_date",
  "paid_end_date",
];

export const sameConditionWindows = (a: ConditionWindows, b: ConditionWindows): boolean =>
  WINDOW_KEYS.every((k) => a[k] === b[k]);

/**
 * The defaults moved (a new policy term, specific-benefit dates or a different
 * policy): replace each window date that still equals the OLD default with the
 * new one, keeping any date the user changed. Returns `w` itself when nothing
 * moved, so callers can skip a state update.
 */
export const reseedConditionWindows = <T extends Record<keyof ConditionWindows, string>>(
  w: T,
  prev: ConditionWindows,
  next: ConditionWindows
): T => {
  const moved = WINDOW_KEYS.filter((k) => toYMD(w[k]) === prev[k] && prev[k] !== next[k]);
  if (moved.length === 0) return w;
  const out = { ...w };
  moved.forEach((k) => {
    out[k] = next[k] as T[typeof k];
  });
  return out;
};

export const CONDITION_WINDOW_SOURCE_HINT: Record<ConditionWindowSource, string> = {
  specific_benefit: "From the policy specific benefit",
  policy_term: "From the policy term (the specific benefit has no window dates)",
  mixed: "From the policy specific benefit, with the policy term where a window is missing",
  none: "No policy dates found; enter the windows",
};
